import type { CSSProperties, ReactNode } from "react";
import { Stack, Typography } from "../mui";
import { NaviiIcon } from "./NaviiIcon";

export type AIWrapperProps = {
  children: ReactNode;
  validationErrors?: Record<string, string | string[] | unknown>;
  label?: string;
  trailing?: ReactNode;
  icon?: ReactNode;
  isSearching?: boolean;
  maxWidth?: number | string;
  className?: string;
};

export const AIWrapper = ({
  children,
  validationErrors,
  label = "AI Powered Assistant",
  trailing,
  icon,
  isSearching,
  maxWidth = "100%",
  className,
}: AIWrapperProps) => {
  const openAiError = validationErrors?.openAI;
  const openAiErrorText = Array.isArray(openAiError) ? openAiError.join(", ") : typeof openAiError === "string" ? openAiError : undefined;

  return (
    <Stack direction="column" alignItems="center" width="100%">
      <div style={{ ...aiWrapperStyles.wrapper, maxWidth }} className={className}>
        <div style={aiWrapperStyles.gradientBorder}>
          <div style={aiWrapperStyles.inner}>
            <div style={aiWrapperStyles.betaBadge}>Beta</div>

            <div style={aiWrapperStyles.iconBox}>
              {icon || <NaviiIcon size={48} isSearching={isSearching} />}
            </div>

            <div style={aiWrapperStyles.textContainer}>
              <span style={aiWrapperStyles.label}>{label}</span>
              {children}
            </div>

            {trailing && (
              <div style={aiWrapperStyles.trailing} className="ai-wrapper-trailing">
                {trailing}
              </div>
            )}
          </div>
        </div>
      </div>

      {openAiErrorText && <Typography color="error">{openAiErrorText}</Typography>}
    </Stack>
  );
};

const aiWrapperStyles: Record<string, CSSProperties> = {
  wrapper: {
    width: "100%",
    minWidth: 0,
    boxSizing: "border-box",
    marginBottom: 16,
    padding: 5,
  },
  gradientBorder: {
    position: "relative",
    padding: 1,
    borderRadius: 16,
    background: "linear-gradient(90deg, #6366f1, #a855f7, #ec4899)",
    boxShadow: "0 6px 20px rgba(0,0,0,0.1)",
  },
  inner: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "12px 16px",
    borderRadius: 16,
    background: "rgba(255,255,255,0.85)",
    backdropFilter: "blur(8px)",
  },
  betaBadge: {
    position: "absolute",
    top: -8,
    right: 12,
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: 0.5,
    padding: "2px 6px",
    borderRadius: 999,
    background: "linear-gradient(90deg, #f59e0b, #ef4444)",
    color: "white",
    boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
    textTransform: "uppercase",
  },
  iconBox: {
    width: 48,
    height: 48,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  textContainer: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
  },
  label: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: "#6b7280",
    marginBottom: 4,
  },
  trailing: {
    marginLeft: "auto",
    fontSize: 12,
    color: "#9ca3af",
    display: "none",
    whiteSpace: "nowrap",
  },
};

if (typeof document !== "undefined") {
  const styleId = "ai-wrapper-keyframes";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.innerHTML = `
      @media (min-width: 640px) {
        .ai-wrapper-trailing { display: block; }
      }
    `;
    document.head.appendChild(style);
  }
}
